import {view, Divider, tokens, Icon} from '@sail/ui';
import {person} from '@sail/icons/react/Icon';
import {defineMessages, useIntl} from 'react-intl';
import {AgentCallResponse} from '@stripe-internal/conversations-client';
import {useState} from 'react';
import formatAmount from '../../../utils/formatAmount';
import {useWidget} from '../../../Widget/useWidget';

type CustomerCardProps = {
  agentCallResponse: AgentCallResponse;
};

const messages = defineMessages({
  cardTitle: {
    id: 'conversations_ui.customer_card.title',
    description: 'Customer Card title',
    defaultMessage: 'Customer',
  },
  totalSpend: {
    id: 'conversations_ui.customer_card.total_spend',
    description: 'Customer Card total spend header',
    defaultMessage: 'Total spend',
  },
  lastPaymentDate: {
    id: 'conversations_ui.customer_card.last_payment_date',
    description: 'Customer Card last payment date header',
    defaultMessage: 'Last payment',
  },
});

export const CustomerCard = ({agentCallResponse}: CustomerCardProps) => {
  const intl = useIntl();
  const widget = useWidget();
  const [isHovered, setIsHovered] = useState(false);

  const {customer} = agentCallResponse;
  const {
    id,
    name,
    email,
    total_spend_amount: totalSpendAmount,
    total_spend_currency: totalSpendCurrency,
    last_payment_date: lastPaymentDate,
  } = customer || {};

  if (!name && !email) {
    return null;
  }

  return (
    <view.div
      css={{
        backgroundColor: 'surface',
        display: 'flex',
        alignItems: 'center',
        borderRadius: 'medium',
        paddingTop: 'small',
        paddingBottom: 'small',
        paddingLeft: 'space.150',
        paddingRight: 'space.150',
        borderColor: 'border',
        borderWidth: 1,
        borderStyle: 'solid',
        maxWidth: '400px',
        position: 'relative',
        transition: 'box-shadow 0.5s',
        ':hover': {
          cursor: 'pointer',
          boxShadow: tokens.shadow.large.toString(),
        },
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => {
        widget.requestAction({
          name: 'navigate',
          data: {
            href: `/customers/${id}`,
          },
        });
      }}
    >
      <view.div
        css={{
          stack: 'y',
          gap: 'small',
          width: '100%',
          minWidth: 0,
        }}
      >
        <view.div
          css={{
            stack: 'x',
            gap: tokens.space[75].toString(),
            alignItems: 'center',
          }}
        >
          <Icon
            icon={person}
            size="xsmall"
            css={{color: isHovered ? 'action.primary' : 'subdued'}}
          />
          <view.span
            css={{
              color: 'subdued',
              font: 'label.small.emphasized',
            }}
          >
            {intl.formatMessage(messages.cardTitle)}
          </view.span>
          {name && email ? (
            <view.div css={{height: 10, stack: 'x', width: 0}}>
              <Divider />
            </view.div>
          ) : null}
          {name && email ? (
            <view.span
              css={{
                font: 'label.small',
                color: 'subdued',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {email}
            </view.span>
          ) : null}
        </view.div>
        <view.div
          css={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          <view.span
            css={{
              font: 'heading.large',
            }}
          >
            {name || email}
          </view.span>
        </view.div>
        <view.div css={{stack: 'x', gap: 'large'}}>
          <view.div
            css={{
              stack: 'y',
              gap: 'xxsmall',
            }}
          >
            <view.span css={{font: 'label.small.emphasized'}}>
              {intl.formatMessage(messages.totalSpend)}
            </view.span>
            <view.span css={{font: 'label.small', color: 'subdued'}}>
              {totalSpendAmount
                ? formatAmount(totalSpendAmount, totalSpendCurrency)
                : 'N/A'}
            </view.span>
          </view.div>
          <view.div
            css={{
              stack: 'y',
              gap: 'xxsmall',
            }}
          >
            <view.span css={{font: 'label.small.emphasized'}}>
              {intl.formatMessage(messages.lastPaymentDate)}
            </view.span>
            <view.span css={{font: 'label.small', color: 'subdued'}}>
              {totalSpendAmount ? lastPaymentDate : 'N/A'}
            </view.span>
          </view.div>
        </view.div>
      </view.div>
    </view.div>
  );
};
